import React, { useEffect, useState } from "react";
import { Route, Redirect, useLocation, useRouteMatch } from "react-router-dom";
import { ApplicationPaths, QueryParameterNames } from "./ApiAuthorizationConstants";
import authService from "./AuthorizeService";
import { CircularLoader } from "../atoms/Loaders";
import { profileApiV3 } from "../../redux/profileApiV3";
import { useDispatch } from "react-redux";
import { useJourneyRouter } from "../hooks/useJourneyRouter";
import { getProgrammeById, getJourneyBySlug } from "../../utils/JourneyUtils";

interface Props {
  path: any;
  exact: any;
  location: any;
  component: any;
}

export default function AuthorizeRoute(props: Readonly<Props>) {
  const location = useLocation();
  let match = useRouteMatch({
    path: props.path,
    exact: props.exact,
  });
  const dispatch = useDispatch();
  const [authState, setAuthState] = useState({
    ready: false,
    authenticated: false,
    accountState: "Unknown",
    referralSource: "Unknown",
  });
  const [profile, setProfile] = useState();
  const [programmeSlug, setProgrammeSlug] = useState(match?.params?.programmeSlug);

  const journey = getJourneyBySlug(profile, programmeSlug);

  // FF logic
  useEffect(() => {
    async function populateAuthenticationState() {
      const user = await authService.getUser();
      let accountState = "Unknown";
      let referralSource = "Unknown";
      let authenticated = user && !!user.sub;
      if (authenticated) {
        localStorage.setItem("userID", user.sub);
        const profileQuery: any = await dispatch(profileApiV3.endpoints.getProfileById.initiate(user.sub));
        if (profileQuery?.isSuccess && profileQuery?.data) {
          accountState = profileQuery.data?.accountState;
          referralSource = profileQuery.data?.currentJourneys[0]?.referralSource;
          if (programmeSlug) {
            const currentJourney = getJourneyBySlug(profileQuery.data, programmeSlug);
            referralSource = currentJourney?.referralSource;
          } else if (profileQuery.data?.currentJourneys?.length === 1) {
            // If we have a single journey we want to be able to route the user to the next logical
            // page during onboarding even when there is no programme slug in the URL.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            let pSlug = getProgrammeById(profileQuery.data?.currentJourneys[0]?.programmeId)?.slug;
            setProgrammeSlug(pSlug);
          }
          setProfile(profileQuery.data);
        }
      }
      setAuthState(state => ({
        ...state,
        ready: true,
        authenticated,
        accountState,
        referralSource,
      }));
    }

    async function authenticationChanged() {
      setAuthState({
        ready: false,
        authenticated: false,
        accountState: "Unknown",
        referralSource: "Unknown",
      });
      await populateAuthenticationState();
    }

    let _subscription = authService.subscribe(() => authenticationChanged());
    populateAuthenticationState();

    return () => authService.unsubscribe(_subscription);
  }, [dispatch, programmeSlug]);

  // CONDITIONAL REDIRECTING
  useJourneyRouter(authState, profile, programmeSlug);

  const { ready, authenticated, accountState, referralSource } = authState;

  let link = document.createElement("a");

  link.href = `${location.pathname}${location.search}${location.hash}`;
  const returnUrl = `${link.protocol}//${link.host}${link.pathname}${link.search}${link.hash}`;
  const redirectUrl = `${ApplicationPaths.Login}?${QueryParameterNames.ReturnUrl}=${encodeURI(returnUrl)}`;

  if (!ready) {
    return <CircularLoader />;
  } else {
    const { component: Component, ...rest } = props;
    return (
      <Route
        {...rest}
        render={renderProps => {
          if (authenticated && accountState === "EmailNotVerified") {
            if (props.path === "/account/verify-email") {
              return <Component {...renderProps} />;
            }
            return <Redirect to="/account/verify-email" />;
          } else if (authenticated && accountState === "ProfileCompleted") {
            // TODO: Move profile completed check to journey object - https://dev.azure.com/ThriveTribe/AWM/_workitems/edit/21630
            if (
              !journey &&
              (props.path.includes(`/profile-builder`) || props.path.includes(`/sign-up/profile-builder`))
            ) {
              return <Redirect to="/" />;
            }
            return <Component {...renderProps} />;
          } else if (authenticated && programmeSlug && accountState === "EmailVerified") {
            if (referralSource === "SelfReferral" || referralSource === "Crm") {
              if (props.location.pathname === `/sign-up/profile-builder/${programmeSlug}`) {
                return <Component {...renderProps} />;
              }
              return <Redirect to={`/sign-up/profile-builder/${programmeSlug}`} />;
            } else {
              if (props.location.pathname === `/profile-builder/${programmeSlug}`) {
                return <Component {...renderProps} />;
              }
              return <Redirect to={`/profile-builder/${programmeSlug}`} />;
            }
          } else if (!authenticated) {
            return <Redirect to={redirectUrl} />;
          } else {
            throw new Error("Unable to verify client profile completion state!");
          }
        }}
      />
    );
  }
}
